// verifySession.ts — what happens to a /verify-code answer after it arrives.
//
// Separate from the component for the same reason otpCells.ts is: this portal
// has no React testing library, so a rule that lives inside a `.tsx` async
// handler cannot be tested at all. The rule here is an ORDERING, and ordering
// is exactly the kind of thing that regresses silently — so the effects are
// injected and the sequence is asserted directly.
//
// THE PROBLEM IT SOLVES. A verify request can still be in flight when the user
// clicks "Change email". Applying its answer afterwards signs them in as the
// address they just left, on a form now showing a different one: authenticated
// as the wrong account, with nothing on screen to say so. The form bumps an
// attempt counter whenever the user moves on (see isCurrentAttempt), and this
// sequence checks it TWICE, because there are two awaits and therefore two
// windows:
//
//   1. after the HTTP round trip, before anything is installed;
//   2. after `setSession` resolves — and that one cannot simply return, because
//      the session is by then already written to browser storage.
//
// A first version of this fix had only check 1 and read as complete.

import { isCurrentAttempt } from './otpCells.ts';

export type SessionTokens = { access_token: string; refresh_token: string };

/**
 * Everything `installSession` is allowed to touch, so a test can watch the
 * order it touches them in.
 *
 * `currentAttempt` is a FUNCTION, not a number: the whole point is that it can
 * change while an await is pending, which a captured value could never show.
 */
export type InstallSessionEffects = {
  /** Writes the session into the Supabase client (and its storage). */
  setSession: (tokens: SessionTokens) => Promise<{ error: unknown }>;
  /**
   * Undoes a local install. MUST be a local-scope sign-out: the default scope
   * is global, which revokes the refresh token server-side and would sign that
   * account out of every other device it is logged into. We are unwinding one
   * browser's storage, not punishing the account.
   */
  signOutLocal: () => Promise<void>;
  /** Hands the bearer to the token holder api.ts reads. */
  publishToken: (accessToken: string) => void;
  /** Sends the user into the portal. */
  navigate: () => void;
  /** The attempt the form is on right now. Read, never captured. */
  currentAttempt: () => number;
};

/**
 * Install a verified session — or discard it, if the form has moved on.
 *
 * Returns what it did, so the caller (and a test) can tell the two apart. A
 * `setSession` failure is THROWN rather than swallowed: the caller's catch
 * already renders it, and a silent failure here would leave the user looking at
 * a form that accepted their code and did nothing.
 */
export async function installSession(
  tokens: SessionTokens,
  startedAt: number,
  fx: InstallSessionEffects,
): Promise<'installed' | 'discarded'> {
  // Window 1: the user left while the POST was on the wire. Nothing has been
  // written yet, so there is nothing to undo.
  if (!isCurrentAttempt(startedAt, fx.currentAttempt())) return 'discarded';

  const { error } = await fx.setSession(tokens);
  if (error) throw error;

  // Window 2: the user left while setSession was resolving. The session IS
  // written now, so returning alone would leave a session for the abandoned
  // address in storage for the next visit.
  if (!isCurrentAttempt(startedAt, fx.currentAttempt())) {
    await fx.signOutLocal();
    return 'discarded';
  }

  // Only now is anything authenticated: the token reaches api.ts's holder and
  // the router moves. Both are skipped on either discard.
  fx.publishToken(tokens.access_token);
  fx.navigate();
  return 'installed';
}
